import React from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import dayjs from 'dayjs';
import { Chip, Link as MuiLink, Typography } from '@mui/material';
import { BiCalendarEvent, BiEditAlt } from 'react-icons/bi';
import { GetMobileServiceAppointmentsQuery } from '../../mobileService/queries/getMobileServiceAppointments.query.generated';

import Table, { Column } from '@components/TableComponents/Table';

import { formatAppointmentType } from '../utils/appointmentType';

type Appointment = GetMobileServiceAppointmentsQuery['appointments'][number];

// Status chips. Keys are lowercase to match the DB convention (per
// event-handlers/docs/appointment-lifecycle.md and the @hopdrive/sdk-appointments
// lowercase fix). Covers both consumer-pay and mobile-service / b2b lifecycles.
const STATUS_CHIPS: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'info' | 'error' }> = {
  // Shared lifecycle (consumer-pay + b2b + mobile-service)
  new: { label: 'New', color: 'info' },
  pending: { label: 'Pending', color: 'warning' },
  ready: { label: 'Ready', color: 'success' },
  canceled: { label: 'Canceled', color: 'error' },
  // Consumer-pay specific
  paying: { label: 'Paying', color: 'info' },
  paid: { label: 'Paid', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  refunded: { label: 'Refunded', color: 'default' },
  // B2B / sparkserv / mobile-service terminal states
  completed: { label: 'Completed', color: 'success' },
  showed: { label: 'Showed', color: 'info' },
  missed: { label: 'Missed', color: 'error' },
  duplicate: { label: 'Duplicate', color: 'default' },
};

const formatTime = (time?: string | null) => {
  if (!time) return '—';
  return dayjs(time).format('M/D/YYYY h:mm A');
};

const formatVehicle = (appt: Appointment) => {
  const parts = [appt.vehicle_year, appt.vehicle_make, appt.vehicle_model].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : '—';
};

// Walk service_orders → stop → run and dedupe to surface every distinct
// mobility run this appointment is linked to. In practice an appointment
// maps to a single run + stop, but the query/data model permits >1 so we
// render each as its own link.
type AppointmentWithRuns = Appointment & {
  service_orders?: Array<{
    id: number;
    stop?: {
      id: number;
      stop_order?: number | null;
      run?: { id: number; status?: string | null } | null;
    } | null;
  }>;
};

interface LinkedRun {
  runId: number;
  /** stop_order is what RunDetailPage's selectedStopOrder state expects.
   * Used as the `?stop=` query param so the deep link opens the stop
   * detail drawer on arrival. May be null if the FK exists but stop_order
   * isn't populated; the URL omits the param in that case. */
  stopOrder: number | null;
}

const getLinkedRuns = (appt: Appointment): LinkedRun[] => {
  const orders = (appt as AppointmentWithRuns).service_orders ?? [];
  // Use a Map so multiple service_orders pointing at the same run still
  // produce a single link, and we keep the first non-null stop_order we
  // see for that run as the drawer target.
  const byRun = new Map<number, LinkedRun>();
  for (const o of orders) {
    const rid = o.stop?.run?.id;
    if (rid == null) continue;
    const so = o.stop?.stop_order ?? null;
    const existing = byRun.get(rid);
    if (!existing) byRun.set(rid, { runId: rid, stopOrder: so });
    else if (existing.stopOrder == null && so != null) {
      existing.stopOrder = so;
    }
  }
  return Array.from(byRun.values());
};

// Map table column names to DB field names for server-side sorting
const columnToDbFieldMap: Record<string, string> = {
  id: 'id',
  type: 'type',
  appointment_time: 'appointment_time',
  consumer: 'consumer_name',
  phone: 'consumer_phone',
  vehicle: 'vehicle_year',
  status: 'status',
};

const dbFieldToColumnMap: Record<string, string> = Object.entries(columnToDbFieldMap).reduce(
  (acc, [col, db]) => { acc[db] = col; return acc; },
  {} as Record<string, string>,
);

interface AppointmentsTableProps {
  appointments: Appointment[];
  totalCount: number;
  orderBy: string;
  order: 'asc' | 'desc';
  setOrderBy: (v: string) => void;
  setOrder: (v: 'asc' | 'desc') => void;
  setOffset: (v: number) => void;
  checkedItems?: Appointment[];
  setCheckedItems?: (items: Appointment[]) => void;
  /** Optional per-row gate. When provided and returns false for an
      appointment, that row's selection checkbox renders disabled.
      Forwarded to the underlying generic Table component. */
  isRowSelectable?: (appointment: Appointment) => boolean;
   /** Show the "Run" column. Only the Mobile Service view fetches the
      service_orders → stop → run data this column reads, so it stays off
      for the Valet/consumer table (where it would render all "—"). */
  showRunColumn?: boolean
  /** Enables the row-actions (⋮) menu with an "Edit Appointment" item.
      Only 'new' appointments show the edit item — once an appointment is
      in a run its details have been copied to the run's stop and edits
      here wouldn't propagate. The Valet/consumer table omits the prop →
      no actions column. */
  onEdit?: (appointment: Appointment) => void;
  /** Whether the "Edit Appointment" item renders; the host gates this on the product's manage
      capability. The details item stays either way. Default true. */
  editAllowed?: boolean;
  /** Hide the Type column. The Mobile Service view filters to a single
      type, so the column carries no information there and its width
      pushes the actions column into horizontal scroll. */
  hideTypeColumn?: boolean;
}

const AppointmentsTable: React.FC<AppointmentsTableProps> = ({
  appointments,
  totalCount,
  orderBy,
  order,
  setOrderBy,
  setOrder,
  setOffset,
  checkedItems,
  setCheckedItems,
  isRowSelectable,
  showRunColumn = false,
  onEdit,
  editAllowed = true,
  hideTypeColumn = false,
}) => {
  const navigate = useNavigate();
  const tableOrderBy = dbFieldToColumnMap[orderBy] || orderBy;
  const selectable = Boolean(checkedItems && setCheckedItems);

  // Row-actions (⋮) menu, following the LocationsTable pattern. Only wired
  // when the caller passes onEdit (the mobile-service page). The details
  // action doubles as row navigation there, since selectable mode disables
  // onRowClick. Edit hides on rows past 'new' — those details were already
  // copied onto the run's stop at dispatch and edits wouldn't propagate.
  const rowActions = React.useMemo(
    () =>
      onEdit
        ? [
            {
              name: 'appointment-details',
              label: 'Go To Appointment Details',
              icon: <BiCalendarEvent style={{ marginRight: 8, fontSize: 20 }} />,
              // Table types row-action callbacks as (data: object); the row
              // data is always an Appointment here.
              handler: (a: object) => navigate(`/appointments/${(a as Appointment).id}`),
            },
            {
              name: 'edit-appointment',
              label: 'Edit Appointment',
              icon: <BiEditAlt style={{ marginRight: 8, fontSize: 20 }} />,
              handler: (a: object) => onEdit(a as Appointment),
              hide: (a: object) => !editAllowed || ((a as Appointment).status || '').toLowerCase() !== 'new',
            },
          ]
        : undefined,
    [onEdit, editAllowed, navigate]
  );

  const handleOrderChange = (newOrder: 'asc' | 'desc' | false) => {
    if (newOrder === false) return;
    setOrder(newOrder);
    setOffset(0);
  };

  const handleOrderByChange = (columnName: string) => {
    const dbField = columnToDbFieldMap[columnName] || columnName;
    setOrderBy(dbField);
    setOffset(0);
  };

  return (
    <Table
      data={appointments}
      order={order}
      orderBy={tableOrderBy}
      onOrderChange={handleOrderChange}
      onOrderByChange={handleOrderByChange}
      onRowClick={selectable ? undefined : (row: any) => navigate(`/appointments/${(row as Appointment).id}`)}
      tableAriaLabel='appointments'
      stickyHeader
      usePagination
      hidePagination
      totalRecordsCount={totalCount}
      checkedItems={checkedItems}
      // Generic Table works in `object`s; this is the one boundary where the
      // typed Appointment[] narrows back from it.
      setCheckedItems={setCheckedItems ? items => setCheckedItems(items as Appointment[]) : undefined}
      isRowSelectable={isRowSelectable as ((data: any) => boolean) | undefined}
      rowActions={rowActions}
    >
      {selectable ? (
        <Column
          name='select'
          checkbox
          label=''
          style={{ width: 48, maxWidth: 48 }}
        />
      ) : null}

      <Column
        name='id'
        value={(a: Appointment) => a.id}
        renderer={(a: Appointment) => (
          <Typography variant='body2' sx={{ fontWeight: 600 }}>
            {a.id}
          </Typography>
        )}
        label='ID'
        style={{ minWidth: 80 }}
      />

      {hideTypeColumn ? null : (
        <Column
          name='type'
          value={(a: Appointment) => a.type}
          renderer={(a: Appointment) => formatAppointmentType(a.type)}
          label='Type'
          style={{ minWidth: 140 }}
        />
      )}

      <Column
        name='appointment_time'
        value={(a: Appointment) => a.appointment_time}
        renderer={(a: Appointment) => formatTime(a.appointment_time)}
        label='Appointment Time'
        style={{ minWidth: 160 }}
      />

      <Column
        name='consumer'
        value={(a: Appointment) => a.consumer_name}
        renderer={(a: Appointment) => a.consumer_name || '—'}
        label='Consumer'
        style={{ minWidth: 160 }}
      />

      <Column
        name='phone'
        value={(a: Appointment) => a.consumer_phone}
        renderer={(a: Appointment) => a.consumer_phone || '—'}
        label='Phone'
        style={{ minWidth: 140 }}
      />

      <Column
        name='vehicle'
        value={(a: Appointment) => formatVehicle(a)}
        renderer={(a: Appointment) => formatVehicle(a)}
        label='Vehicle'
        style={{ minWidth: 180 }}
      />

      <Column
        name='status'
        value={(a: Appointment) => a.status}
        renderer={(a: Appointment) => {
          const cfg = STATUS_CHIPS[a.status] || { label: a.status, color: 'default' as const };
          return <Chip label={cfg.label} size='small' color={cfg.color} variant='outlined' sx={{ fontWeight: 600 }} />;
        }}
        label='Status'
        style={{ minWidth: 110 }}
      />

      {showRunColumn ? (
        <Column
          name='run'
          value={(a: Appointment) => getLinkedRuns(a).map(r => r.runId).join(',')}
          renderer={(a: Appointment) => {
            const runs = getLinkedRuns(a);
            if (runs.length === 0) {
              return <Typography variant='body2' color='text.disabled'>—</Typography>;
            }
            return (
              <>
                {runs.map(({ runId, stopOrder }, idx) => {
                  const target =
                    stopOrder != null
                      ? `/mobile-service/runs/${runId}?stop=${stopOrder}`
                      : `/mobile-service/runs/${runId}`;
                  return (
                    <React.Fragment key={runId}>
                      {idx > 0 && ', '}
                      <MuiLink
                        component={RouterLink}
                        to={target}
                        // Stop row-click propagation so the link doesn't also
                        // trigger the row's onRowClick (which navigates to the
                        // appointment detail page).
                        onClick={(e) => e.stopPropagation()}
                        underline='hover'
                        sx={{ fontWeight: 600 }}
                      >
                        Run #{runId}
                      </MuiLink>
                    </React.Fragment>
                  );
                })}
              </>
            );
          }}
          label='Run'
          style={{ minWidth: 110 }}
        />
      ) : null}

    </Table>
  );
};

export default React.memo(AppointmentsTable);
